import { useCallback } from 'react';
import { getUsersApiError, storeEPassiToken } from '../../clients/users-api';
import { useUser } from '../../hooks/useUser';
import { useAddError } from '../error/ErrorContext';
import { ErrorMessage } from '../../model/errors/ErrorMessage';
import { errorCode } from '../../model/errors';
import { trackInsightsException } from '../../utils/telemetry/appinsights';
import { useClearEPassiTokenStatus } from './useEPassiTokenStatus';

type StoreEPassiWalletToken = {
  // Resolves to false when storing failed; the error has already been surfaced.
  storeToken: (args: { walletToken: string; validUntil?: string | null }) => Promise<boolean>;
  reportRejected: () => void;
  reportFailed: () => void;
};

// Shared by all three token acquisition paths: the web return route, the iOS
// deep-link return handler and the Android activity-result path. Never log or
// track the token value — only relay it to users-api.
export const useStoreEPassiWalletToken = (): StoreEPassiWalletToken => {
  const { user } = useUser();
  const addError = useAddError();
  const clearTokenStatus = useClearEPassiTokenStatus();

  const storeToken = useCallback(
    async ({
      walletToken,
      validUntil,
    }: {
      walletToken: string;
      validUntil?: string | null;
    }): Promise<boolean> => {
      if (!user) return false;

      try {
        await storeEPassiToken({ walletToken, validUntil: validUntil ?? undefined });
        await clearTokenStatus(user.userId);
        return true;
      } catch (error) {
        const apiError = getUsersApiError(error);
        addError(new ErrorMessage(errorCode.EPASSI_LINK, {}, apiError.message));
        trackInsightsException('FRONTEND_EPASSI_STORE_TOKEN_ERROR', apiError, {
          userId: user.userId,
        });
        return false;
      }
    },
    [addError, clearTokenStatus, user]
  );

  const reportRejected = useCallback((): void => {
    addError(new ErrorMessage(errorCode.EPASSI_LINK, {}, 'Epassi-valtuutus hylättiin.'));
  }, [addError]);

  const reportFailed = useCallback((): void => {
    addError(
      new ErrorMessage(
        errorCode.EPASSI_LINK,
        {},
        'Epassi-lompakon yhdistäminen epäonnistui. Yritä uudelleen.'
      )
    );
  }, [addError]);

  return { storeToken, reportRejected, reportFailed };
};
